/**
 * How the game page loads the chunks round its camera (ADR-019's amendment of 2026-10-03, P2.0-T6): the values
 * OdysseyTales hands Daedalus's chunk stream, chunk view and prop views, which take them as parameters. A change here
 * is a data edit; `bun run stress:crossings` measures what it does to a crossing.
 */

/**
 * The knobs of chunk loading.
 */
export interface ChunkLoading {
  /**
   * The milliseconds a frame may spend loading and unloading chunks that are off screen. A frame always does at
   * least one unit, and a chunk on screen is finished at once whatever this says.
   */
  readonly budgetMs: number;
  /** The most props one unit of work places or removes. */
  readonly propsPerUnit: number;
  /** The most hidden prop images each prop layer keeps for reuse. */
  readonly propPool: number;
  /** The most released chunk textures kept for reuse; each holds 4 MB of GPU memory at 32 px tiles and 32-tile chunks. */
  readonly texturePool: number;
  /**
   * The most chunk textures the painters a stage ends hand on to the painters of the next stage (`ChunkTexturePool`), so a
   * seam crossing paints the map stepped onto into textures already made instead of allocating a framebuffer for each.
   */
  readonly handover: number;
  /** How long a handed-on texture nobody took is kept before it is destroyed, in milliseconds. */
  readonly handoverMs: number;
}

/**
 * OdysseyTales's values, as measured by P2.0-T6 (the Playbook's entry). `budgetMs` at 3 is half of a 164 Hz frame
 * (6.1 ms): a crossing column of the 5,000 tier (about 1,650 props and 3 textures) loads in about six frames and one
 * of the 10,000 tier in about as many. A unit of 128 props costs about a third of a millisecond to place from the
 * pool, 0.8 ms when its images are created and up to 1.3 ms to destroy; a tile layer's paint is one unit of 1 to 3
 * ms, so the budget is overrun by one unit at most. `propPool` holds a crossing column at the red density budget
 * (ADR-088: 400 props a chunk, 4 chunks) with room to spare; it is not larger because a pooled image stays, hidden,
 * in the world's display list, where 4,096 of them cost a frame 0.1 to 0.3 ms. `texturePool` holds a crossing column
 * of 4 chunks with 3 painted tile layers, 48 MB of GPU memory at most. `handover` covers the chunks a crossing paints in
 * its first frames, about 10 in the first and 20 over the first four, each of which costs 2 to 7 ms to make on a desktop
 * GPU; the textures handed on were live a frame before, so it adds no GPU memory at the crossing, and what is left
 * untaken is freed `handoverMs` later.
 */
export const CHUNK_LOADING: ChunkLoading = { budgetMs: 3, propsPerUnit: 128, propPool: 2048, texturePool: 12, handover: 24, handoverMs: 2000 };
